/**
 * Jeopardy's host screen. Uses `<a href>`, not react-router: a game's UI must not
 * depend on the client's routing.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  JudgmentTray,
  JoinQr,
  Scoreboard,
  Timer,
  platform,
  type GameUiProps,
  TvLink,
} from '@gsf/kit';
import type { JeopardyLockout } from '@gsf/games';
import { jeopardyView, type JeopardyView } from './view';
import { bank, setBankPack } from './content';
import { AnswerList, useCommentSlots } from './Comments';
import { BoardGrid, type AddTarget, type RemoveTarget } from './BoardGrid';
import { BoardQuickStart } from './BoardQuickStart';
import { BuzzList } from './BuzzList';
import { ClueEditorPanel } from './ClueEditorPanel';
import { RoundEditor } from './RoundEditor';
import { RoundTabs } from './RoundTabs';

/**
 * Stores only the id: whether the clue is in play is worked out at render, because it
 * can go live, or be backed out of, while the editor is open.
 */
type EditorTarget = { kind: 'edit'; clueId: string };

/** A blank tile, with `createQuestion`'s own mode and lockout defaults. */
const blank = { prompt: '', answer: '', mode: 'buzzer', lockoutMode: 'host_enable' } as const;

export function JeopardyHost({ snapshot, send, submit, connected, notify }: GameUiProps) {
  const view = useMemo(() => jeopardyView(snapshot), [snapshot]);
  // Ownership, decided by core: whether this host may edit the pack (board/clues) and run
  // session admin (scores, players, rename, TV). A co-host or a built-in pack makes these false.
  const canEdit = snapshot.you.canEditContent;
  const canManage = snapshot.you.canManageSession;
  const [editing, setEditing] = useState<EditorTarget | null>(null);
  const [roundEditorOpen, setRoundEditorOpen] = useState(false);
  // Edit (build the board) vs Play (run the game). A click on a tile edits it in Edit mode
  // and puts it in play in Play mode; building controls are hidden in Play. #24, #8.
  // Open in Edit while the board is still being built (empty or any unwritten tile), in Play
  // once it is complete — the state you most likely want, still switchable by hand.
  const [boardMode, setBoardMode] = useState<'edit' | 'play'>(() => {
    const board = view.shared.board;
    const building = board.length === 0 || board.some((col) => col.cells.some((c) => c.draft));
    return building ? 'edit' : 'play';
  });
  // A non-owner can never enter Edit mode, whatever the build heuristic chose.
  const editMode = canEdit && boardMode === 'edit';
  // On the board, scores/join fold into buttons that open a popup, so the board gets the
  // whole width. During a live clue (phase ≠ board) they sit in the side column, where the
  // host needs them while grading. Shortcuts are only ever a popup (`?`).
  const [infoOpen, setInfoOpen] = useState<'scores' | 'join' | 'shortcuts' | null>(null);
  const [autoOpened, setAutoOpened] = useState<readonly number[]>([]);

  /**
   * Host operations that are HTTP, not game actions. A failure has no socket to arrive on,
   * so it lands in the toast via `notify`.
   */
  const http = useCallback(
    async (what: string, run: () => Promise<unknown>) => {
      try {
        await run();
      } catch (err) {
        notify(err instanceof Error ? err.message : `Could not ${what}.`);
      }
    },
    [notify],
  );

  const { core, shared } = view;
  const phase = shared.phase;
  const clueMode = shared.current?.mode;
  // The board (either mode) spans the full width; the side column is only for a live clue.
  const onBoard = phase === 'board';

  // Edit the pack this session actually reads, not the game's first pack — otherwise a
  // session pinned to another pack (e.g. an imported one) writes clues it can never see,
  // and `addClues` rejects them as "none of those clues exist".
  const segmentPack = snapshot.core.segment?.contentPackId ?? null;
  useEffect(() => {
    setBankPack(segmentPack);
    return () => setBankPack(null);
  }, [segmentPack]);

  // Host shortcuts. Y and N are bound by `VerdictCard`, which renders the ruling.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      // The clue editor is a popup with its own Escape handling; its keys are not the board's.
      if (editing) return;
      // An info popup swallows Escape to close itself, ahead of the board shortcuts.
      if (infoOpen) {
        if (event.key === 'Escape') setInfoOpen(null);
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      switch (event.key.toLowerCase()) {
        case '?':
          setInfoOpen('shortcuts');
          break;
        case ' ':
          event.preventDefault();
          if (phase === 'selected') send('reveal');
          else if (phase === 'revealed' && clueMode === 'buzzer') send('openBuzzers');
          break;
        case 'escape':
          if (phase !== 'board') send('returnToBoard');
          break;
        default:
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, clueMode, send, editing, infoOpen]);

  // Tiles in board order (down each column, left to right), for the editor's Prev/Next.
  const orderedClueIds = useMemo(
    () => shared.board.flatMap((col) => col.cells.map((cell) => cell.clueId)),
    [shared.board],
  );
  const editingIndex = editing ? orderedClueIds.indexOf(editing.clueId) : -1;
  const editingColumn = editing
    ? shared.board.find((col) => col.cells.some((cell) => cell.clueId === editing.clueId))
    : undefined;

  /**
   * Two writes per tile: a clue is a shared pack item and is not on this session's board
   * until `addClues` says so. Clues are written blank; `reduce.ts` refuses to play one.
   * `addClues` once at the end: one broadcast, so a new column arrives whole.
   */
  const layout = useCallback(
    async (target: AddTarget) => {
      await http('add to the board', async () => {
        const written: string[] = [];

        if (target.kind === 'append') {
          written.push((await bank.createQuestion({ ...blank, categoryId: target.categoryId, points: target.points })).id);
        } else if (target.kind === 'column') {
          // Unnamed column: a blank heading beats "Category 3", which looks written.
          const category = await bank.createCategory({ name: '', round: shared.round });
          for (const points of target.points) {
            written.push((await bank.createQuestion({ ...blank, categoryId: category.id, points })).id);
          }
        } else {
          for (const tile of target.tiles) {
            written.push((await bank.createQuestion({ ...blank, ...tile })).id);
          }
        }

        if (written.length > 0) send('addClues', { clueIds: written });
      });
    },
    [http, send, shared.round],
  );

  /** Bulk-write a starting grid on an empty round, batched into one broadcast. */
  const quickStart = useCallback(
    async (columns: number, rows: number) => {
      await http('create the board', async () => {
        const written: string[] = [];
        for (let c = 0; c < columns; c += 1) {
          const category = await bank.createCategory({ name: '', round: shared.round });
          for (let r = 0; r < rows; r += 1) {
            written.push(
              (await bank.createQuestion({ ...blank, categoryId: category.id, points: 100 * (r + 1) })).id,
            );
          }
        }
        if (written.length > 0) send('addClues', { clueIds: written });
      });
      // Auto-open the round editor once per new round, tracked explicitly: "zero clues"
      // does not mean "newly created".
      if (!autoOpened.includes(shared.round)) {
        setAutoOpened((seen) => [...seen, shared.round]);
        setRoundEditorOpen(true);
      }
    },
    [http, send, shared.round, autoOpened],
  );

  /**
   * Resize to `columns × rows`. A blank tile removed is deleted from the bank, a written one
   * only comes off the board; a whole column is checked against the bank before its heading
   * is deleted, because a category can hold clues not on this board.
   */
  const resizeRound = useCallback(
    async (columns: number, rows: number) => {
      await http('resize the round', async () => {
        const current = shared.board;
        const draftOf = new Map(
          current.flatMap((col) => col.cells.map((cell) => [cell.clueId, cell.draft] as const)),
        );
        const added: string[] = [];
        const removed: string[] = [];
        const draftsToDelete: string[] = [];
        const columnsGoing: string[] = [];

        current.forEach((col, ci) => {
          if (ci >= columns) {
            removed.push(...col.cells.map((cell) => cell.clueId));
            columnsGoing.push(col.categoryId);
          } else if (col.cells.length > rows) {
            const cut = col.cells.slice(rows);
            removed.push(...cut.map((cell) => cell.clueId));
            draftsToDelete.push(...cut.filter((cell) => cell.draft).map((cell) => cell.clueId));
          }
        });

        // Grow: fill surviving columns up to `rows`, then add whole new columns.
        for (let ci = 0; ci < Math.min(columns, current.length); ci += 1) {
          const col = current[ci]!;
          for (let r = col.cells.length; r < rows; r += 1) {
            added.push((await bank.createQuestion({ ...blank, categoryId: col.categoryId, points: 100 * (r + 1) })).id);
          }
        }
        for (let ci = current.length; ci < columns; ci += 1) {
          const category = await bank.createCategory({ name: '', round: shared.round });
          for (let r = 0; r < rows; r += 1) {
            added.push((await bank.createQuestion({ ...blank, categoryId: category.id, points: 100 * (r + 1) })).id);
          }
        }

        if (added.length > 0) send('addClues', { clueIds: added });
        if (removed.length > 0) send('removeClues', { clueIds: removed });

        // Bank cleanup. A removed column whose bank clues are all blank goes entirely
        // (heading and cascade); otherwise only its blank board tiles are deleted.
        for (const categoryId of columnsGoing) {
          const inBank = (await bank.listBank(shared.round)).find((c) => c.id === categoryId);
          if (inBank && inBank.questions.every((q) => q.prompt.trim() === '' || q.answer.trim() === '')) {
            await bank.deleteCategory(categoryId);
          } else {
            const col = current.find((c) => c.categoryId === categoryId);
            draftsToDelete.push(...(col?.cells.filter((cell) => cell.draft).map((cell) => cell.clueId) ?? []));
          }
        }
        const uniqueDrafts = [...new Set(draftsToDelete)].filter((id) => draftOf.get(id));
        if (uniqueDrafts.length > 0) await bank.deleteQuestions(uniqueDrafts);
      });
    },
    [http, send, shared.board, shared.round],
  );

  /**
   * Remove means off this board, not out of the bank; only never-written tiles are deleted,
   * which is why none of it asks first. `removeClues` before the deletes, so the state never
   * holds an id that resolves to nothing.
   */
  const unlayout = useCallback(
    async (target: RemoveTarget) => {
      const cells = shared.board.flatMap((column) =>
        target.kind === 'column' && column.categoryId !== target.categoryId ? [] : column.cells,
      );
      const wanted = new Set(
        target.kind === 'clue'
          ? [target.clueId]
          : target.kind === 'row'
            ? target.clueIds
            : cells.map((cell) => cell.clueId),
      );
      const going = cells.filter((cell) => wanted.has(cell.clueId));
      if (going.length === 0) return;

      // Fire-and-forget: the reducer refuses the clue in play, and the refusal arrives as
      // a toast rather than as a half-done delete.
      send('removeClues', { clueIds: going.map((cell) => cell.clueId) });

      const blanks = going.filter((cell) => cell.draft).map((cell) => cell.clueId);

      await http('tidy up those blank tiles', async () => {
        // An all-blank column goes too, but only after asking the bank: `deleteCategory`
        // cascades, and the category can hold clues that are not on this board.
        if (target.kind === 'column') {
          const inBank = (await bank.listBank(shared.round)).find(
            (category) => category.id === target.categoryId,
          );
          if (inBank && inBank.questions.every((q) => q.prompt.trim() === '' || q.answer.trim() === '')) {
            await bank.deleteCategory(target.categoryId);
            return;
          }
        }
        if (blanks.length > 0) await bank.deleteQuestions(blanks);
      });
    },
    [http, send, shared.board, shared.round],
  );

  /**
   * Blank every written tile in this round, keeping columns, headings and values. A blank
   * tile is a draft, so a played one is put back too or it would sit greyed and unwritable.
   */
  const clearRound = useCallback(async () => {
    const cells = shared.board.flatMap((col) => col.cells);
    const written = cells.filter((cell) => !cell.draft);
    if (written.length === 0) return;
    if (!confirm(`Clear all ${written.length} written clues in this round? Columns, headings and values stay. This cannot be undone.`)) return;
    await http('clear this round', async () => {
      for (const cell of written) await bank.updateQuestion(cell.clueId, { prompt: '', answer: '' });
    });
    for (const cell of cells) if (cell.state === 'done') send('reopenClue', { clueId: cell.clueId });
  }, [http, send, shared.board]);

  // How much of the board is still an empty tile.
  const unwritten = shared.board.reduce(
    (total, column) => total + column.cells.filter((cell) => cell.draft).length,
    0,
  );

  // The three info panels' bodies, shared between the side column (live clue) and the
  // board's popups (Play mode).
  const scoresBody = (
    <>
      <Scoreboard
        players={view.standings}
        activeParticipantId={shared.activeParticipantId}
        showPresence
        onAdjust={
          canManage
            ? (participantId, delta) =>
                void http('adjust that score', () =>
                  platform.adjustScore(core.code, participantId, delta),
                )
            : undefined
        }
        onRemove={
          canManage
            ? (participantId) =>
                void http('remove that player', () => platform.removePlayer(core.code, participantId))
            : undefined
        }
        onMerge={
          canManage
            ? (fromId, intoId) =>
                void http('merge those players', () => platform.mergePlayers(core.code, fromId, intoId))
            : undefined
        }
      />
      {canManage && view.standings.length > 1 ? (
        <p className="faint" style={{ marginBottom: 0 }}>
          Same person listed twice? ⇄ folds one row into the other, scores and all.
        </p>
      ) : null}
      <div className="row" style={{ marginTop: '.5rem' }}>
        <label className="field-check" title="Show the scoreboard beside the board on the TV view">
          <input
            type="checkbox"
            checked={shared.scoresDocked}
            onChange={(e) => send('setScoresDocked', { docked: e.target.checked })}
          />
          Dock to TV view
        </label>
        <span className="spacer" />
        <button
          type="button"
          className="btn btn-sm"
          title="Pop the scores up on the TV view for a few seconds"
          onClick={() => send('flashScores')}
        >
          Display scores on TV
        </button>
      </div>
    </>
  );
  const joinBody = <JoinQr code={core.code} small />;
  const shortcutsBody = (
    <p className="faint" style={{ margin: 0, lineHeight: 1.7 }}>
      <kbd>Space</kbd> reveal / unlock buzzers
      <br />
      <kbd>Y</kbd> correct · <kbd>N</kbd> incorrect
      <br />
      <kbd>J</kbd> <kbd>K</kbd> move down / up a grading list
      <br />
      <kbd>Esc</kbd> back to board
      <br />
      <kbd>?</kbd> this list
      {core.displayCanDrive ? (
        <>
          <br />
          <span className="tag">TV</span> the TV view takes <kbd>Space</kbd> too
        </>
      ) : null}
    </p>
  );
  const infoPanels = [
    { key: 'scores' as const, title: 'Scores', body: scoresBody },
    { key: 'join' as const, title: 'Join', body: joinBody },
  ];
  const popups = [...infoPanels, { key: 'shortcuts' as const, title: 'Shortcuts', body: shortcutsBody }];
  const popup = popups.find((p) => p.key === infoOpen);

  return (
    <div className="host">
      <div className="host-bar">
        {shared.phase === 'board' ? (
          // On the board there is no clue to return to; the wordmark above goes home.
          <a className="btn btn-sm btn-ghost" href="/" title="Back to the main page">
            ← Home
          </a>
        ) : (
          // With a clue up, this is where the eye goes to get back to the board (#40) —
          // sending home mid-clue was a trap. Leaving the game is still the wordmark's job.
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            title="Back to the board"
            onClick={() => send('returnToBoard')}
          >
            ← Board
          </button>
        )}
        {canManage ? (
          <RenameTitle
            title={core.title}
            onRename={(name) => http('rename this game', () => platform.renameSession(core.code, name))}
          />
        ) : (
          <strong>{core.title}</strong>
        )}

        <span className="spacer" />
        <span className={`tag ${connected ? 'tag-live' : 'tag-bad'}`}>
          {connected ? 'live' : 'offline'}
        </span>
        <span className="tag">{shared.phase.replace('_', ' ')}</span>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          title="Keyboard shortcuts (?)"
          aria-label="Keyboard shortcuts"
          onClick={() => setInfoOpen('shortcuts')}
        >
          ?
        </button>
        {/* The TV link and the drive toggle are the session owner's: handing out the link is
            a credential, the toggle is authority. A co-host drives over the socket, not these. */}
        {canManage ? (
          <>
            <TvLink code={core.code} />
            <label
              className="field-check"
              title="Make the TV view clickable: click a tile there to put that clue on screen — the prompt only, never the answer. Space unlocks the buzzers, same as here. Scoring stays on this screen."
            >
              <input
                type="checkbox"
                checked={core.displayCanDrive}
                onChange={(e) =>
                  void http('change TV control', () =>
                    platform.setDisplayCanDrive(core.code, e.target.checked),
                  )
                }
              />
              Reveal from TV view
            </label>
          </>
        ) : null}
      </div>

      <div className={`host-body${onBoard ? ' is-board-fullwidth' : ''}`}>
        <div className="host-col">
          {shared.phase === 'board' ? (
            <div className={`panel board-panel${editMode ? ' is-editing' : ''}`}>
              <div className="row board-mode-bar">
                <h2 className="panel-title" style={{ margin: 0 }}>
                  {editMode ? 'Building' : 'Pick a clue'}
                </h2>
                <span className={`tag ${editMode ? 'tag-accent' : 'tag-live'}`}>
                  {editMode ? 'Edit mode' : 'Play mode'}
                </span>
                <span className="spacer" />
                {canEdit ? (
                  <div className="seg-group" role="group" aria-label="Board mode">
                    <button
                      type="button"
                      className={`btn btn-sm${editMode ? ' btn-primary' : ' btn-ghost'}`}
                      aria-pressed={editMode}
                      title="Build the board: click a tile to edit it, add clues, rows and columns"
                      onClick={() => setBoardMode('edit')}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm${!editMode ? ' btn-primary' : ' btn-ghost'}`}
                      aria-pressed={!editMode}
                      title="Run the game: click a tile to put it on screen"
                      onClick={() => setBoardMode('play')}
                    >
                      Play
                    </button>
                  </div>
                ) : (
                  <span className="faint" title="This board belongs to someone else, or is a built-in">
                    Read-only board
                  </span>
                )}
              </div>
              {/* Play-mode: scores/join fold into these buttons, each opening a popup. */}
              {editMode ? (
                <div className="row" style={{ marginTop: '.25rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    title="Blank every clue in this round, keeping the columns and values"
                    disabled={unwritten === shared.board.reduce((n, col) => n + col.cells.length, 0)}
                    onClick={() => void clearRound()}
                  >
                    Clear round…
                  </button>
                </div>
              ) : (
                <div className="row seg-group" style={{ marginTop: '.25rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    title="Put every played tile, in every round, back in play"
                    onClick={() => {
                      if (confirm('Put every played clue, in every round, back in play? Scores stay as they are.')) {
                        send('reopenAll');
                      }
                    }}
                  >
                    Replay board…
                  </button>
                  {infoPanels.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      className="btn btn-sm btn-ghost"
                      onClick={() => setInfoOpen(p.key)}
                    >
                      {p.title}
                    </button>
                  ))}
                </div>
              )}
              <p className="faint" style={{ marginTop: 0 }}>
                {editMode
                  ? 'Select a tile to edit, or use + to add clues, rows and columns.'
                  : 'Select a tile to put it on screen.'}
              </p>
              <div className="row round-strip">
                <RoundTabs
                  rounds={shared.rounds}
                  round={shared.round}
                  onPick={(round) => send('setRound', { round })}
                  onAddRound={
                    editMode
                      ? () =>
                          send('setRound', {
                            round: Math.max(...shared.rounds.map((r) => r.round)) + 1,
                          })
                      : undefined
                  }
                  onRename={
                    editMode
                      ? (round, name) =>
                          void http('rename that round', () => bank.renameRound(round, name))
                      : undefined
                  }
                />
                {editMode ? (
                  <button
                    type="button"
                    className={`btn btn-sm round-editor-toggle${roundEditorOpen ? ' is-open' : ''}`}
                    aria-pressed={roundEditorOpen}
                    title="Bulk-edit every clue in this round, and its size"
                    onClick={() => setRoundEditorOpen((open) => !open)}
                  >
                    Round editor
                  </button>
                ) : null}
              </div>
              {editMode && shared.board.length === 0 ? <BoardQuickStart onCreate={quickStart} /> : null}
              <BoardGrid
                board={shared.board}
                className="host-board"
                editMode={editMode}
                onPick={editMode ? undefined : (clueId) => send('selectClue', { clueId })}
                onReopen={editMode ? undefined : (clueId) => send('reopenClue', { clueId })}
                onEdit={editMode ? (clueId) => setEditing({ kind: 'edit', clueId }) : undefined}
                onAdd={editMode ? (target) => void layout(target) : undefined}
                onRemove={editMode ? (target) => void unlayout(target) : undefined}
                onNameColumn={
                  editMode
                    ? (categoryId, name) =>
                        void http('rename that column', () => bank.updateCategory(categoryId, { name }))
                    : undefined
                }
                currentClueId={shared.current?.clueId ?? null}
              />
              <p className="faint" style={{ marginBottom: 0 }}>
                {editMode ? (
                  <>
                    {unwritten > 0 ? (
                      <>
                        <strong className="board-todo">
                          {unwritten} tile{unwritten === 1 ? '' : 's'} still to write
                        </strong>{' '}
                        — an unwritten tile cannot be played. Click a tile to write it.
                        <br />
                      </>
                    ) : null}
                    + adds a question, × removes one.
                  </>
                ) : (
                  <>
                    {core.displayCanDrive
                      ? 'TV control is on — you can pick clues from the TV view instead. '
                      : ''}
                    Greyed tiles have been played — click one to put it back in play.
                  </>
                )}
              </p>
            </div>
          ) : (
            <CluePanel
              view={view}
              send={send}
              submit={submit}
              onComment={(answerId, comment) => send('comment', { answerId, comment })}
              onEdit={(clueId) => setEditing({ kind: 'edit', clueId })}
            />
          )}

          {shared.phase !== 'board' ? (
            <div className="panel">
              <h2 className="panel-title">Buzz order</h2>
              <BuzzList
                buzzes={view.buzzes}
                activeParticipantId={shared.activeParticipantId}
                onPick={(participantId) => send('setActivePlayer', { participantId })}
              />
            </div>
          ) : null}
        </div>

        {onBoard ? null : (
          <div className="host-col">
            {infoPanels.map((p) => (
              <div className="panel" key={p.key}>
                <h2 className="panel-title">{p.title}</h2>
                {p.body}
              </div>
            ))}
          </div>
        )}
      </div>

      {editing ? (
        <ClueEditorPanel
          key={editing.clueId}
          clueId={editing.clueId}
          categoryName={editingColumn?.name ?? ''}
          inPlay={shared.current?.clueId === editing.clueId && shared.phase !== 'board'}
          hasPrev={editingIndex > 0}
          hasNext={editingIndex >= 0 && editingIndex < orderedClueIds.length - 1}
          onPrev={() =>
            editingIndex > 0 && setEditing({ kind: 'edit', clueId: orderedClueIds[editingIndex - 1]! })
          }
          onNext={() =>
            editingIndex >= 0 &&
            editingIndex < orderedClueIds.length - 1 &&
            setEditing({ kind: 'edit', clueId: orderedClueIds[editingIndex + 1]! })
          }
          onClose={() => setEditing(null)}
          onSaved={() => undefined}
          notify={notify}
        />
      ) : null}

      {shared.phase === 'board' && roundEditorOpen ? (
        <RoundEditor
          key={shared.round}
          round={shared.round}
          board={shared.board}
          onResize={resizeRound}
          onClose={() => setRoundEditorOpen(false)}
          notify={notify}
        />
      ) : null}

      {popup ? (
        <div className="modal-backdrop" onClick={() => setInfoOpen(null)}>
          <div
            className="modal"
            role="dialog"
            aria-label={popup.title}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sheet-head">
              <h2 className="panel-title" style={{ margin: 0 }}>
                {popup.title}
              </h2>
              <span className="spacer" />
              <button className="btn btn-sm btn-ghost" onClick={() => setInfoOpen(null)}>
                Close
              </button>
            </div>
            {popup.body}
          </div>
        </div>
      ) : null}

    </div>
  );
}

/**
 * The game's name in the host bar, editable in place. Commits on blur and Enter, reverts on
 * Escape; the rename broadcasts, so the new title arrives back through the snapshot.
 */
function RenameTitle({
  title,
  onRename,
}: {
  title: string;
  onRename: (name: string) => void;
}) {
  const [editing, setEditing] = useState(false);

  if (!editing) {
    return (
      <button
        className="host-title"
        title="Rename this game"
        aria-label={`Rename game, currently ${title}`}
        onClick={() => setEditing(true)}
      >
        <strong>{title}</strong>
        <span className="host-title-pencil" aria-hidden="true">✎</span>
      </button>
    );
  }

  function commit(next: string) {
    setEditing(false);
    const name = next.trim();
    if (name && name !== title) onRename(name);
  }

  return (
    <input
      className="host-title-input"
      defaultValue={title}
      aria-label="Game name"
      maxLength={80}
      autoFocus
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          e.currentTarget.value = title;
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** The clue in play, plus whatever the server is asking the host about. */
function CluePanel({
  view,
  send,
  submit,
  onComment,
  onEdit,
}: {
  view: JeopardyView;
  send: GameUiProps['send'];
  submit: GameUiProps['submit'];
  onComment: (participantId: string, comment: string) => void;
  onEdit: (clueId: string) => void;
}) {
  const { core, shared } = view;
  const clue = shared.current;
  const slots = useCommentSlots(view.answers, onComment);

  if (!clue) {
    return (
      <div className="panel">
        <p className="muted">No clue selected.</p>
        <button className="btn" onClick={() => send('returnToBoard')}>
          Back to board
        </button>
      </div>
    );
  }

  const setLockout = (lockout: JeopardyLockout) => send('setLockout', { lockout });

  return (
    <div className="panel clue-panel">
      <div className="clue-meta">
        <span className="clue-category">{clue.categoryName}</span>
        <span className="clue-value">{clue.points}</span>
        {clue.mode !== 'buzzer' ? (
          <span className="tag tag-accent">
            {clue.isFinal ? 'Final round' : clue.mode === 'open' ? 'Open answer' : 'Wager clue'}
          </span>
        ) : null}
        <Timer endsAt={core.deadlineAt} serverNow={core.serverNow} variant="chip" />
        <span className="spacer" />
        {/* Editable at any point in the clue's life, not just from the board. */}
        <button
          className="btn btn-sm clue-edit-btn"
          title="Fix a typo, change the answer, or swap this clue out"
          onClick={() => onEdit(clue.clueId)}
        >
          Edit clue
        </button>
      </div>

      <p className="clue-prompt">{view.prompt}</p>
      <p className="clue-answer">
        <strong>Answer:</strong> {view.clueAnswer}
      </p>

      {/* Lockout choice only matters for buzzer clues, before buzzing starts. */}
      {clue.mode === 'buzzer' ? (
        <div className="row" style={{ marginTop: '0.75rem' }}>
          <span className="faint">Buzzers:</span>
          <button
            className={`btn btn-sm${shared.lockout === 'host_enable' ? ' btn-primary' : ''}`}
            disabled={!['selected', 'revealed'].includes(shared.phase)}
            onClick={() => setLockout('host_enable')}
          >
            Host unlocks
          </button>
          <button
            className={`btn btn-sm${shared.lockout === 'auto_unlock' ? ' btn-primary' : ''}`}
            disabled={!['selected', 'revealed'].includes(shared.phase)}
            onClick={() => setLockout('auto_unlock')}
          >
            Unlock on reveal
          </button>
        </div>
      ) : null}

      {/* Daily double: whoever picked the tile plays it alone. */}
      {shared.phase === 'wager' && shared.wager ? (
        <div className="judge-strip">
          <span className="judge-who">{view.nameOf(shared.wager.participantId)}</span>
          <span className="muted">
            {view.wagerAmount === null ? 'is entering a wager…' : `wagered ${view.wagerAmount}`}
          </span>
          <span className="spacer" />
          <span className="faint">wrong player? tap another name below</span>
        </div>
      ) : null}

      {/* Final round: everyone bets at once, secretly; amounts stay hidden. */}
      {shared.phase === 'final_wager' ? (
        <div className="col" style={{ marginTop: '0.75rem' }}>
          <span className="faint">
            {shared.finalWagers.filter((w) => w.placed).length} of {view.standings.length} have
            wagered
          </span>
        </div>
      ) : null}

      {/* Answers arriving, before there is anything to grade (see `Comments.tsx`). */}
      {shared.phase === 'collecting' ? (
        <div className="col" style={{ marginTop: '0.75rem' }}>
          <span className="faint">
            {view.answers.length} of {view.standings.length} submitted
          </span>
          <AnswerList answers={view.answers} slots={slots} />
        </div>
      ) : null}

      {/* Settled: the answers stay on screen, marked right or wrong, after the reveal. */}
      {shared.phase === 'resolved' && view.answers.length > 0 ? (
        <div className="col" style={{ marginTop: '0.75rem' }}>
          <span className="faint">Answers</span>
          <AnswerList answers={view.answers} slots={slots} />
        </div>
      ) : null}

      {/* Every host decision. `slots` adds the private reply per graded answer, a
          `comment` action rather than a judgment. */}
      <JudgmentTray
        requests={view.requests}
        onDecide={submit}
        nameOf={view.nameOf}
        slots={slots}
      />

      <div className="flow-buttons">
        {shared.phase === 'selected' ? (
          <button className="btn btn-primary btn-lg" onClick={() => send('reveal')}>
            Reveal to board (Space)
          </button>
        ) : null}

        {shared.phase === 'revealed' && clue.mode === 'buzzer' ? (
          <button className="btn btn-primary btn-lg" onClick={() => send('openBuzzers')}>
            Unlock buzzers (Space)
          </button>
        ) : null}

        {['buzzers_open', 'answering'].includes(shared.phase) ? (
          <button className="btn" onClick={() => send('closeBuzzers')}>
            Lock buzzers
          </button>
        ) : null}

        {shared.phase === 'buzzers_open' ? (
          <span className="tag tag-live" style={{ alignSelf: 'center' }}>
            buzzers live
          </span>
        ) : null}

        {shared.phase === 'final_wager' ? (
          <button className="btn btn-primary btn-lg" onClick={() => send('openFinalAnswers')}>
            Reveal clue &amp; open answers
          </button>
        ) : null}

        {shared.phase === 'collecting' ? (
          <button className="btn btn-primary" onClick={() => send('closeAnswers')}>
            Close answers now
          </button>
        ) : null}

        {/* The only button that spends a clue nobody won; "Back to board" puts the tile
            back. */}
        {shared.phase !== 'resolved' && shared.phase !== 'wager' && shared.phase !== 'final_wager' ? (
          <button
            className="btn"
            title="Nobody got it — show the answer and spend the clue"
            onClick={() => send('revealAnswer')}
          >
            Nobody knows
          </button>
        ) : null}

        <span className="spacer" />
        <button
          className={`btn${shared.phase === 'resolved' ? ' btn-primary btn-lg' : ''}`}
          onClick={() => send('returnToBoard')}
        >
          {shared.phase === 'resolved' ? 'Next clue →' : 'Back to board (Esc)'}
        </button>
      </div>
    </div>
  );
}
